import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@/lib/i18n'
import { trackPageView } from '@/lib/analytics'
import { applyPageTitle } from '@/lib/pageTitle'

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page: ReactNode) => (<Layout>{page}</Layout>)

    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )
    // Set the tab title, then report the page view -- in that order, so the
    // view is reported with the title the user actually sees. The Umami tracker
    // is loaded with auto page views disabled, so trackPageView is the only
    // thing that reports them, including the initial load.
    applyPageTitle(props.initialPage.component)
    trackPageView()

    router.on('navigate', (event) => {
      applyPageTitle(event.detail.page.component)
      trackPageView()
    })

    // Hide the inline splash once React has scheduled its first commit. A
    // single rAF lets the first paint happen so the user sees app content
    // appear *under* the splash before it fades, rather than getting a flash
    // of blank background between the two.
    requestAnimationFrame(() => {
      const splash = document.getElementById("app-splash")
      if (!splash) return
      splash.classList.add("hidden")
      // Drop the node from the DOM after the fade so it can't intercept any
      // hover/scroll events on slow devices.
      setTimeout(() => splash.remove(), 280)
    })
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
